// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/CitedClaimsPanel.test.tsx
// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/CommentCitationLinks.test.tsx
import { ArrowSquareOut } from "@phosphor-icons/react";
import { Button, Dialog, Flex, Text } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Preview } from "../../../components/Preview";
import {
  KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
  knowledgeDatabaseRoute,
} from "../../../routes/settings/routes/knowledge-database/route";
import {
  type TicketMessageCitation,
  isKnowledgeRuleCitation,
} from "../utils/commentSourceUtils";

/**
 * Shared Knowledge Database rule citation dialog — statement headline, quote
 * excerpt, and deep link into the Rules tab. Used by comment chips and the
 * Cited Claims rail so both surfaces stay in sync.
 */
export function KnowledgeRuleCitationDialog({
  citation,
  open,
  onOpenChange,
  children,
}: {
  citation: TicketMessageCitation;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Optional Dialog.Trigger (or other) children. Omit for controlled-only open. */
  children?: ReactNode;
}) {
  const { t } = useTranslation("ui");
  if (!isKnowledgeRuleCitation(citation)) return null;

  const statement = citation.statement?.trim();
  const quote = citation.quote?.trim();
  const sourceName = citation.sourceName?.trim();

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {children}
      <Dialog.Content
        data-testid="comment-knowledge-rule-citation-dialog"
        className="flex max-h-[min(70vh,420px)] flex-col gap-4 overflow-y-auto"
      >
        <Dialog.Title>
          {t("commentCitationLinks.knowledgeRuleSource")}
        </Dialog.Title>
        <Dialog.Description className="hidden">
          {t("commentCitationLinks.knowledgeRuleExcerpt")}
        </Dialog.Description>
        {statement ? (
          <Text size="2" weight="medium" data-testid="knowledge-rule-statement">
            {statement}
          </Text>
        ) : null}
        {sourceName ? (
          <Text
            size="2"
            className="text-puntt-neutral-gray-11"
            data-testid="knowledge-rule-citation-source-doc"
          >
            {sourceName}
          </Text>
        ) : null}
        {quote ? (
          <>
            <Text size="2" className="text-puntt-neutral-gray-11">
              <em>{t("commentCitationLinks.fromKnowledgeDatabase")}</em>
            </Text>
            <Preview text={quote} />
          </>
        ) : null}
        <Flex justify="end">
          <Button variant="outline" size="2" asChild>
            <Link
              data-testid="comment-knowledge-rule-citation-view-link"
              to={knowledgeDatabaseRoute.to}
              search={{
                ...KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
                tab: "rules",
                ruleId: citation.ruleId,
                ...(citation.sourceId ? { sourceId: citation.sourceId } : {}),
              }}
              target="_blank"
              rel="noreferrer"
              className="puntt-link flex gap-1"
            >
              <Text>
                {t("commentCitationLinks.viewRuleInKnowledgeDatabase")}
              </Text>
              <ArrowSquareOut width={16} height={16} />
            </Link>
          </Button>
        </Flex>
        <Dialog.Close>
          <Button type="button" variant="soft" color="gray">
            {t("commentCitationLinks.close")}
          </Button>
        </Dialog.Close>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export function knowledgeRuleCitationLabel(
  citation: TicketMessageCitation,
  fallback: string,
): string {
  if (!isKnowledgeRuleCitation(citation)) return fallback;
  return citation.statement?.trim() || citation.quote?.trim() || fallback;
}
